import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolvePath, useLocation, useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useTheme } from 'styled-components';

import { pages } from 'pages';
import { useSendAccessCode, useVerifyAccessCode } from 'api/account';
import { toast } from 'shared/components/Toast/Toast';
import { Header } from 'modules/auth/components/Header/Header';
import {
  VerificationPinForm,
  VerificationPinFormInput,
} from 'modules/auth/components/VerificationPinForm/VerificationPinForm';
import { AuthBox, AuthWrapper } from 'modules/auth/Auth.styles';
import useUser from 'hooks/useUser';
import { datadogRum } from '@datadog/browser-rum';
import { getDashboardRedirectPath } from 'modules/auth/Auth.utils';
import { HttpStatusCode } from 'axios';
import { FlagKey } from '../../../../api/types';
import { useFeatureFlags } from '../../../../hooks/useFeatureFlags';

const VERIFICATION_TIME_SECONDS = 10 * 60; // 10 minutes

export function Verification() {
  const [searchParams] = useSearchParams();

  const [email] = useState(searchParams.get('email') || '');
  const [timeLeft, setTimeLeft] = useState(VERIFICATION_TIME_SECONDS);
  const [isNewCode, setIsNewCode] = useState(false);
  const [pinResetKey, setPinResetKey] = useState(`verificationPin-${0}`);

  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const { setUserResult } = useUser();
  const isMFA = location.state?.isMFA || undefined;

  const {
    flags: { [FlagKey.onboardingRefresh]: onboardingRefresh },
  } = useFeatureFlags();

  useEffect(() => {
    if (!email) {
      navigate(`/${pages.REGISTRATION}`);
    }
  }, [email, navigate]);

  useEffect(() => {
    let timerId: NodeJS.Timeout;

    if (timeLeft > 0) {
      timerId = setInterval(() => {
        setTimeLeft((prevTime) => prevTime - 1);
      }, 1000);
    }

    return () => clearInterval(timerId);
  }, [timeLeft]);

  const { mutate: sendAccessCode } = useSendAccessCode({
    onSuccess: () => {
      const newResetKey = `verificationPin-${parseInt(pinResetKey.split('-').at(-1) || '', 10) + 1}`;
      setTimeLeft(VERIFICATION_TIME_SECONDS);
      setPinResetKey(newResetKey);
      if (!isNewCode) {
        setIsNewCode(true);
      } else {
        toast({
          type: 'success',
          title: t('toast.success'),
          message: t('toast.newCodeSent', { email }),
          theme,
        });
      }
    },
    onError: ({ status }) => {
      let toastMessage;
      let isError = true;
      switch (status) {
        case HttpStatusCode.TooManyRequests:
          toastMessage = t('toast.tooManyAttempts');
          break;
        case HttpStatusCode.TooEarly:
          toastMessage = t('toast.tooSoon');
          isError = false;
          break;
        default:
          toastMessage = t('toast.somethingWentWrong');
      }

      toast({
        type: isError ? 'error' : 'warning',
        title: isError ? t('toast.error') : t('toast.warning'),
        message: toastMessage,
        theme,
      });
    },
  });

  const {
    mutate: verifyAccessCode,
    error: verifyAccessCodeError,
    isLoading,
  } = useVerifyAccessCode({
    onSuccess: (response, variables) => {
      if (isMFA) {
        const { user } = response.data;
        setUserResult({
          user,
          loading: false,
          selectedAgreement: user?.agreements[0],
        });
        datadogRum.setUser({
          id: user.id,
          email: user.email,
          name: `${user.first_name} ${user.last_name}`,
          role: user.role,
        });
        const { hasDashboardAccess, redirectPath } = getDashboardRedirectPath(
          user,
          user.agreements?.[0],
          onboardingRefresh,
        );
        if (hasDashboardAccess) {
          navigate(resolvePath(pages.OVERVIEW, `/${pages.DASHBOARD}`));
        } else navigate(redirectPath);
      } else {
        const params = new URLSearchParams({
          email: variables.email,
          access_code: variables.access_code,
        });

        navigate(`/${pages.PASSWORD}?${params.toString()}`);
      }
    },
    onError: ({ response }) => {
      const status = response?.status;
      const unCaughtError = status !== 400 && status !== 422 && status !== 429;

      if (status === 429) {
        navigate(`/${pages.TOO_MANY_ATTEMPTS}`);
      }

      if (unCaughtError) {
        toast({
          type: 'error',
          title: t('toast.error'),
          message: t('toast.somethingWentWrong'),
          theme,
        });
      }
    },
  });

  const handleSubmitPinForm = ({ pin }: VerificationPinFormInput) => {
    verifyAccessCode({
      email,
      access_code: pin,
      is_MFA: isMFA,
    });
  };

  const handleResendPin = () => {
    sendAccessCode({ email });
  };

  let pinErrorMessage: string | undefined;
  if (verifyAccessCodeError?.response?.status === 400) {
    pinErrorMessage = t('verification.wrongCode');
  }

  if (verifyAccessCodeError?.response?.status === 422) {
    pinErrorMessage = t('verification.expiredCode');
  }

  const headerTitle = isMFA
    ? t(isNewCode ? 'verification.newCode' : 'verification.verifyYour')
    : t('verification.checkYour');
  const headerTitleBold = isMFA
    ? t(isNewCode ? 'verification.sent' : 'verification.account')
    : t('verification.email');

  return (
    <AuthWrapper>
      <AuthBox>
        <Header title={headerTitle} titleBold={headerTitleBold} showLogo />
        <VerificationPinForm
          email={email.toLowerCase()}
          timeLeftSeconds={timeLeft}
          verifying={isLoading}
          isMFA={Boolean(isMFA)}
          isNewCode={isNewCode}
          pinError={pinErrorMessage}
          onSubmitForm={handleSubmitPinForm}
          onResendPin={handleResendPin}
          key={pinResetKey}
        />
      </AuthBox>
    </AuthWrapper>
  );
}

export default Verification;
